import useApp from 'useApp';

import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { SPLITIO_KEY } from 'types/technicianAvailability.type';

import { ApolloProvider } from '@apollo/client';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';

import ErrorBoundary from '@/components/core/ErrorBoundary';
import ErrorFallback from '@/components/core/ErrorFallback';
import { PurchaseJourneyContentProvider } from '@/content/purchase-journey-content';
import { StorageNames } from '@/lib/constants';
import { AnalyticsProvider } from '@/lib/gatrack';
import { ContentfulRoutes } from '@/pages/contentful-layout';
import MaintenancePage from '@/pages/maintenance';
import { client } from '@/services/client/services';
import { useCartStore, useCustomerStore, useProductStore } from '@/stores';

import ScrollToTop from './ScrollToTop';
import { SentryUserIntegration } from './components/user/sentryuserintegration';
import { useQualtrics } from './hooks/useQualitrics';
import { useSplitIO } from './hooks/useSplitIO';
import { AUTH0_CONFIG } from './lib/config';

// Generate a unique sessionId for this tab
if (!sessionStorage.getItem('sessionId')) {
  sessionStorage.setItem('sessionId', crypto.randomUUID());
}

const SalesforceChatNew = React.lazy(() =>
  import('./components/core/chatNew').then(module => ({ default: module.SalesforceChatNew }))
);

const AppContent: React.FC = () => {
  const location = useLocation();
  const [isQualtricsSurvicateEanbled] = useSplitIO(SPLITIO_KEY.SKYWEB_ENABLE_QUALTRICS);
  const [isMaintenanceMode] = useSplitIO(SPLITIO_KEY.MAINTENANCE_MODE_NBJ);
  const [isDeferredReady, setIsDeferredReady] = useState(false);
  const maintenanceContent =
    location.pathname === '/' ? <MaintenancePage /> : <Navigate to="/" replace />;

  const { clearCustomerStore, ctAPIStatus } = useCustomerStore(state => state);
  const { clearCart } = useCartStore(state => state);
  const { resetProducts } = useProductStore(state => state);

  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const hasAttemptedSilentAuthRef = useRef(false);
  const hasCheckedCustomerExpiryRef = useRef(false);

  const checkCustomerSessionExpiry = useCallback(() => {
    const {
      customerId: persistedCustomerId,
      kkExpireAt: persistedKkExpireAt,
      ctAPIStatus: persistedCtAPIStatus,
    } = useCustomerStore.getState();

    const currentDateTimeStamp = Date.now();

    const isExpired = persistedKkExpireAt != null && persistedKkExpireAt < currentDateTimeStamp;

    if ((persistedCustomerId && isExpired) || persistedCtAPIStatus) {
      void clearCart();
      void resetProducts();
      clearCustomerStore();

      sessionStorage.removeItem(StorageNames.ORDER_STORAGE);
      sessionStorage.removeItem(StorageNames.PRODUCT_STORAGE);
      localStorage.removeItem(StorageNames.CUSTOMER_STORAGE);

      if (location.pathname.includes('/build-my-plan')) {
        window.location.href = '/';
      }
    }
  }, [clearCart, resetProducts, clearCustomerStore, location.pathname]);

  useEffect(() => {
    if (!hasCheckedCustomerExpiryRef.current) {
      hasCheckedCustomerExpiryRef.current = true;
      checkCustomerSessionExpiry();
    } else if (ctAPIStatus) {
      checkCustomerSessionExpiry();
    }
  }, [ctAPIStatus, checkCustomerSessionExpiry]);

  useQualtrics(isQualtricsSurvicateEanbled);

  useEffect(() => {
    if (isLoading || isAuthenticated || hasAttemptedSilentAuthRef.current) {
      return;
    }

    hasAttemptedSilentAuthRef.current = true;

    // Pick up an existing shared SSO session so header auth state is correct after landing from My Account.
    void getAccessTokenSilently().catch(() => {
      // No active SSO session for this SPA, leave the user as logged out.
    });
  }, [getAccessTokenSilently, isAuthenticated, isLoading]);

  useEffect(() => {
    if (globalThis.window === undefined) return;
    let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;

    const schedule = () => {
      timeoutId = globalThis.setTimeout(() => setIsDeferredReady(true), 2000);
    };

    if (document.readyState === 'complete') {
      schedule();
      return () => {
        if (timeoutId) globalThis.clearTimeout(timeoutId);
      };
    }

    const onLoad = () => {
      schedule();
    };

    globalThis.addEventListener('load', onLoad, { once: true });

    return () => {
      globalThis.removeEventListener('load', onLoad);
      if (timeoutId) globalThis.clearTimeout(timeoutId);
    };
  }, []);

  // Reload this tab when another tab adds a bundle to the cart.
  useEffect(() => {
    const channel = new BroadcastChannel('sky-bundle-sync');
    channel.onmessage = (event: MessageEvent) => {
      if (
        event.data?.type === 'BUNDLE_SELECTED' &&
        event.data?.senderTabId !== sessionStorage.getItem('sessionId')
      ) {
        // Only reload if the user hasn't progressed past the plan tab
        const query = new URLSearchParams(window.location.search);
        const tab = query.get('tab');
        const isPastPlanTab = tab && tab !== 'plan';
        if (!isPastPlanTab) {
          // Clear stale cart from sessionStorage so the reload fetches the latest cart from CT API
          sessionStorage.removeItem('order-store');
          window.location.reload();
        }
      }
    };
    return () => channel.close();
  }, []);

  useEffect(() => {
    const checkoutChatClassName = 'route-build-my-plan-checkout';
    const isCheckoutRoute = location.pathname.startsWith('/build-my-plan/checkout');

    document.body.classList.toggle(checkoutChatClassName, isCheckoutRoute);

    return () => {
      document.body.classList.remove(checkoutChatClassName);
    };
  }, [location.pathname]);

  return (
    <>
      <ScrollToTop />
      <SentryUserIntegration />
      {isMaintenanceMode ? (
        maintenanceContent
      ) : (
        <>
          <ContentfulRoutes className="z-12 sticky bg-color-midnight-blue-midnight100" />
          {isDeferredReady ? (
            <Suspense fallback={null}>
              <SalesforceChatNew chatType="sales" />
            </Suspense>
          ) : null}
        </>
      )}
    </>
  );
};

const App: React.FC = () => {
  const { onRedirectCallback } = useApp();

  return (
    <ErrorBoundary fallback={error => <ErrorFallback error={error} />}>
      <ApolloProvider client={client}>
        <Auth0Provider
          domain={AUTH0_CONFIG.domain}
          clientId={AUTH0_CONFIG.clientId}
          authorizationParams={{
            redirect_uri: globalThis.location.origin,
            audience: AUTH0_CONFIG.audience,
          }}
          onRedirectCallback={onRedirectCallback}
        >
          <AnalyticsProvider
            debug={true}
            defaultPageProperties={{
              environment: process.env.REACT_APP_DEPLOY_ENV?.trim(),
              pageType: 'demo',
            }}
          >
            <PurchaseJourneyContentProvider>
              <AppContent />
            </PurchaseJourneyContentProvider>
          </AnalyticsProvider>
        </Auth0Provider>
      </ApolloProvider>
    </ErrorBoundary>
  );
};

export default App;
